import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationService } from "../../services";
import { asQuery } from "../../services/common";
import { usePersistStore, useStore } from "../../store";

// Foreground poll for the bell. refetchInterval pauses while the tab is
// backgrounded (refetchIntervalInBackground defaults to false), and
// refetchOnWindowFocus refreshes when the user returns — matching the old
// visibilitychange-based polling.
const NOTIF_POLL_MS = 30 * 1000;

export function useNotifications(token?: string) {
  return useQuery({
    queryKey: ["notifications", token],
    queryFn: () => asQuery<any[]>(NotificationService.list(token as string)),
    enabled: !!token,
    refetchInterval: NOTIF_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: (notificationId: string) =>
      asQuery<any>(
        NotificationService.markAsRead(token, { notificationId }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: () => asQuery<any>(NotificationService.markAllAsRead(token)),
    onSuccess: () => {
      setSuccessMessage("All notifications marked as read.");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}
